/* Gallery: one eager picture, staged loading, and a clock that stops offscreen. */
(() => {
  'use strict';
  if (customElements.get('mr-gallery-hero')) return;
  class MaxRiveGalleryHero extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      const hero = this;
      const slides = [...hero.querySelectorAll('.mrg-slide')];
      const selectors = [...hero.querySelectorAll('.mrg-destination')];
      if (!slides.length || selectors.length !== slides.length) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const on = (target, type, fn, options = {}) => target?.addEventListener(type, fn, { ...options, signal });
      const playback = hero.querySelector('.mrg-playback');
      const status = hero.querySelector('[data-mrg-announcement]');
      const motion = matchMedia('(prefers-reduced-motion: reduce)');
      const duration = 8000;
      let current = 0, elapsed = 0, last = 0, request = 0;
      let paused = motion.matches, hovering = false, focused = false, visible = true;
      let touch = null, loading = false, pageHidden = false;
      const canRun = () => !paused && !hovering && visible && !document.hidden &&
        !pageHidden && (!focused || document.activeElement === playback) && !loading;
      const halt = () => { cancelAnimationFrame(this.frame); this.frame = 0; last = 0; };
      const paint = () => selectors[current].style.setProperty('--mrg-progress', String(Math.min(elapsed / duration, 1)));
      const start = () => {
        halt();
        if (hero.isConnected && canRun()) this.frame = requestAnimationFrame(tick);
      };
      const playbackState = () => {
        hero.classList.toggle('mrg-is-paused', paused);
        playback?.setAttribute('aria-label', paused ? 'Play slideshow' : 'Pause slideshow');
      };
      const stop = () => { paused = true; playbackState(); halt(); };
      function activate(next, manual) {
        slides.forEach((slide, i) => {
          const active = i === next;
          slide.classList.toggle('mrg-is-active', active);
          slide.inert = !active;
          slide.setAttribute('aria-hidden', String(!active));
          selectors[i].classList.toggle('mrg-is-current', active);
          selectors[i].style.removeProperty('--mrg-progress');
          if (active) selectors[i].setAttribute('aria-current', 'true');
          else selectors[i].removeAttribute('aria-current');
        });
        current = next; elapsed = 0; paint();
        hero.querySelector('[data-mrg-current]').textContent = String(current + 1).padStart(2, '0');
        if (manual && status) status.textContent = slides[current].getAttribute('aria-label');
      }
      function show(index, manual = false) {
        const next = (index + slides.length) % slides.length, token = ++request;
        if (manual) stop();
        if (next === current || window.MRLandingMedia?.ready(slides[next])) {
          loading = false; activate(next, manual); start(); return;
        }
        loading = true; halt();
        window.MRLandingMedia?.ensure(slides[next], manual ? 'auto' : 'low').then(success => {
          if (token !== request || signal.aborted) return;
          loading = false;
          if (success) activate(next, manual);
          else { elapsed = 0; paint(); if (manual && status) status.textContent = 'This photograph could not load. Please try another slide.'; }
          start();
        });
      }
      const tick = time => {
        this.frame = 0;
        if (!hero.isConnected || !canRun()) { last = 0; return; }
        elapsed += last ? Math.min(time - last, 100) : 0; last = time; paint();
        if (elapsed >= duration / 2) window.MRLandingMedia?.ensure(slides[(current + 1) % slides.length]);
        if (elapsed >= duration) { show(current + 1); return; }
        this.frame = requestAnimationFrame(tick);
      };
      selectors.forEach((button, i) => on(button, 'click', () => show(i, true)));
      on(hero.querySelector('.mrg-previous'), 'click', () => show(current - 1, true));
      on(hero.querySelector('.mrg-next'), 'click', () => show(current + 1, true));
      on(playback, 'click', () => { paused = !paused; elapsed = 0; playbackState(); start(); });
      on(hero, 'keydown', event => {
        if (!['ArrowRight', 'ArrowLeft'].includes(event.key)) return;
        event.preventDefault(); show(current + (event.key === 'ArrowRight' ? 1 : -1), true);
      });
      on(hero, 'pointerenter', event => { if (event.pointerType === 'mouse') { hovering = true; halt(); } });
      on(hero, 'pointerleave', () => { hovering = false; start(); });
      on(hero, 'focusin', event => { if (event.target !== playback) { focused = true; stop(); } });
      on(hero, 'focusout', event => { if (!hero.contains(event.relatedTarget)) { focused = false; start(); } });
      on(hero, 'touchstart', event => { if (event.touches.length === 1) touch = {x:event.touches[0].clientX,y:event.touches[0].clientY}; }, { passive:true });
      on(hero, 'touchend', event => {
        if (!touch) return;
        const dx=event.changedTouches[0].clientX-touch.x,dy=event.changedTouches[0].clientY-touch.y;
        if (Math.abs(dx)>45 && Math.abs(dx)>Math.abs(dy)*1.3) show(current+(dx<0?1:-1),true);
        touch=null;
      }, { passive:true });
      on(hero, 'touchcancel', () => { touch=null; }, { passive:true });
      on(motion, 'change', event => { if (event.matches) stop(); });
      on(document, 'visibilitychange', start);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => { visible=entries[0].isIntersecting; start(); });
        this.observer.observe(hero);
      }
      on(window, 'pagehide', () => { pageHidden=true; halt(); });
      on(window, 'pageshow', event => { if(event.persisted){pageHidden=false;start();} });
      activate(0,false); playbackState(); start();
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller=null;
      this.observer?.disconnect(); cancelAnimationFrame(this.frame);
    }
  }
  customElements.define('mr-gallery-hero', MaxRiveGalleryHero);
})();
